import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import { Box, ListItem, ListItemButton, Tooltip, Typography, useTheme } from '@mui/material';
import { CSSProperties, FC, ReactNode } from 'react';

import { User, UserMin } from '@apiContract';
import { Label, LabelColor } from '@components/Label';
import UserListDisplay from '@uiKit/UserListDisplay';

import { sxCutText, sxIcon, sxItemButton, sxLocationBox } from './styles';

export type BadgeProp = {
  text: string;
  color: LabelColor;
};

type ListItemProps = {
  selected: boolean;
  style: CSSProperties;
  item: User | UserMin;
  onClick?: () => void;
  badge?: BadgeProp;
  useTitle?: boolean;
  blocked?: boolean;
  blockedTitle?: string;
};

type WrapperProps = {
  blocked?: boolean;
  onClick?: () => void;
  selected: boolean;
  children: ReactNode;
  blockedTitle?: string;
};

const ItemWrapper: FC<WrapperProps> = ({ blocked, onClick, selected, blockedTitle, children }) => {
  const theme = useTheme();

  const content =
    blocked || !onClick ? (
      <Box
        p={1}
        width="100%"
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        sx={{ opacity: blocked ? 0.5 : 1 }}
      >
        {children}
      </Box>
    ) : (
      <ListItemButton onClick={onClick} sx={{ ...sxItemButton(selected, theme) }}>
        {children}
      </ListItemButton>
    );

  return (
    <Tooltip title={blocked && blockedTitle ? blockedTitle : null} arrow>
      {content}
    </Tooltip>
  );
};

const Item: FC<ListItemProps> = ({
  style,
  selected,
  item,
  onClick,
  badge,
  blocked = false,
  useTitle = false,
  blockedTitle,
}) => {
  let location;

  if ('locationName' in item) {
    location = item.locationName;
  } else if ('location' in item) {
    location = item.location?.name;
  }

  return (
    <ListItem component="div" sx={{ ...style }} disablePadding>
      <ItemWrapper onClick={onClick} blocked={blocked} selected={selected} blockedTitle={blockedTitle}>
        <UserListDisplay
          opacity={selected}
          user={item}
          AvatarChild={selected || blocked ? <CheckOutlinedIcon sx={sxIcon} /> : null}
          useTitle={useTitle}
          style={{ flex: '1', minWidth: 0 }}
        />
        <Box sx={sxLocationBox}>
          {location && (
            <Tooltip title={location} arrow>
              <Typography color="text.secondary" variant="body2" textAlign="right" sx={sxCutText}>
                {location}
              </Typography>
            </Tooltip>
          )}
          {badge && (
            <Label variant="soft" color={badge.color} sx={{ ml: 2 }}>
              {badge.text}
            </Label>
          )}
        </Box>
      </ItemWrapper>
    </ListItem>
  );
};

export default Item;
